/* Copyright (c) 2020-present CloudIO Inc */

import {
  selector,
  useStateXAction,
  useStateXAddToUndo,
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXGetter,
  useStateXSelector,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { Button, Typography } from '@mui/material';
import { activeItemSelector } from 'designer/activeItemSelector';
import { designPath, draggingAtom, pageAtom } from 'designer/atoms';
import { deleteChildrenItem, deleteItem } from 'designer/state';
import { cleanupOrphanItemsAction } from 'layout/Sidebar/panels/TreeView/state';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo } from 'react';
import useUser from 'root/useUser';
import {
  activePinnedSidebarPanelSelector,
  activeSidebarPanelAtom,
  contextMenuAtom,
} from '../../atoms';
import Tree from './Tree';

export const itemsCountSelector = selector({
  path: ['transient', 'designer', 'itemsCount', ':appUid', ':pageId'],
  get: ({ get, params }) => {
    const page = get(pageAtom, { params });
    return Object.keys(page ?? {}).length;
  },
});

export function OrphanItems({
  appUid,
  pageId,
}: {
  appUid: string;
  pageId: string;
}): React.ReactElement {
  const treename = 'designer';
  const { userName } = useUser();
  const setPoint = useStateXAtomSetter(contextMenuAtom);
  const addToUndo = useStateXAddToUndo('io');
  const platform = usePlatform();
  const cleanupOrphanItems = useStateXAction(cleanupOrphanItemsAction);
  const dragging = useStateXAtomValue(draggingAtom);
  const setActiveSidebarPanel = useStateXAtomSetter(activeSidebarPanelAtom);
  const get = useStateXGetter();
  const set = useStateXSetter();

  const onClick = (
    e: React.SyntheticEvent<HTMLElement, MouseEvent>,
    itemId: string,
  ) => {
    e.stopPropagation();
    set(
      activeItemSelector,
      { appUid, pageId, itemId, expand: false, treename, event: e.nativeEvent },
      { params: { appUid, pageId, treename }, props: platform },
    );
  };

  const designer = treename === 'designer';
  const onDobuleClick = designer
    ? (e: React.MouseEvent<HTMLDivElement, MouseEvent>, itemId: string) => {
        set(
          activeItemSelector,
          { appUid, pageId, itemId, expand: false, treename },
          { params: { appUid, pageId, treename }, props: platform },
        );
        if (
          get(activePinnedSidebarPanelSelector, {
            params: { userName },
          }) !== 'props'
        ) {
          setActiveSidebarPanel('props');
        }
      }
    : undefined;

  const onContextMenu = designer
    ? (e: React.MouseEvent<HTMLDivElement, MouseEvent>, itemId: string) => {
        e.stopPropagation();
        e.preventDefault();
        set(
          activeItemSelector,
          { appUid, pageId, itemId, expand: false, treename },
          { params: { appUid, pageId, treename }, props: platform },
        );
        setPoint({
          appUid,
          pageId,
          itemId,
          left: e.clientX,
          top: e.clientY,
          orphan: true,
          dsAlias: '',
        });
      }
    : undefined;

  // rerender when any item is added or removed
  useStateXSelector(itemsCountSelector, { params: { appUid, pageId } });

  if (dragging) {
    return <></>;
  }
  const page = get(pageAtom, { params: { appUid, pageId } });
  let ids: string[] = [];
  if (page) {
    const _page = { ...page };
    deleteItem(_page, 'root');
    ids = Object.keys(_page ?? {});
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      deleteChildrenItem(_page, id);
    }
    ids = Object.keys(_page ?? {});
  }
  if (!pageId || ids.length === 0) {
    return <></>;
  }
  return (
    <>
      <Typography variant="subtitle1" color="error">
        Orphan Items{' '}
        <Button
          onClick={() => {
            addToUndo(designPath({ appUid, pageId }), pageId);
            cleanupOrphanItems({ appUid, platform, pageId });
          }}
        >
          Cleanup
        </Button>
      </Typography>
      {ids.map((id) => (
        <Tree
          key={id}
          pageId={pageId}
          itemId={id}
          first
          last
          root
          orphan
          level={0}
          treename={treename}
          onClick={onClick}
          onDoubleClick={onDobuleClick}
          onContextMenu={onContextMenu}
          platform={platform}
          appUid={appUid}
        />
      ))}
    </>
  );
}

export default memo(OrphanItems);
